// ----------------------------------------------------------------------
// <copyright file="Izenda.PermissionDetail.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

!(function() {
  var m = require('mobx');
  var trackable = require('share/tracking/Izenda.Trackable');
  var uiState = require('logic/Izenda.UiState');

  const {
    filter,
    get,
    getOr,
    isNil,
    isPlainObject,
    omit,
    concat,
    cloneDeep,
    isArrayLike,
    isObject,
    isBoolean
  } = require('lodash/fp');

  var ROLE_EXCLUDED = [
    'simpleDataSources',
    'advancedDataSources',
    'isTenantSetup',
    'tenantAccess',
    'isClickedSection',
    'propsCloned',
    'section',
    'isDirty',
    'dataSources'
  ];

  var TENANT_EXCLUDED = [
    'simpleDataSources',
    'advancedDataSources',
    'isTenantSetup',
    'tenantAccess',
    'isClickedSection',
    'propsCloned',
    'section',
    'isDirty',
    'tenantSetup',
    'dataSources',
    'fullReportAndDashboardAccess'
  ];

  var TENANT_ACCESS_EXCLUDE = ['propsCloned', 'tenantSetup', 'categoryAccessibility', 'displayDashboardTileHeader'];

  /**
   * Get omit keys list by tenant permission, role permission
   * @returns:  {array} list omit key
   */
  const getOmitList = function (tenantPermission) {
    const isFullAccessForRoleIsGranted =
      getOr(true, 'roleSetup.grantRoleWithFullReportAndDashboardAccess.value', tenantPermission);
    if (!isFullAccessForRoleIsGranted) {
      return isNil(uiState.tenantId)
        ? concat(ROLE_EXCLUDED, ['fullReportAndDashboardAccess'])
        : concat(ROLE_EXCLUDED, ['fullReportAndDashboardAccess', 'tenantSetup']);
    }
    return isNil(uiState.tenantId) ? ROLE_EXCLUDED : concat(ROLE_EXCLUDED, ['tenantSetup']);
  };

  /**
   * Check tenant permission: return true if tenantPermision or key null.
   * @param: {Object}   tenantPermission
   * @param: {String}   key
   */
  var checkTenantPermissionByKey = function(tenantPermision, key) {
    if (isNil(tenantPermision) || isNil(key)) {
      return true;
    }

    return tenantPermision[key];
  };

  function PermissionDetail(data, isTenantSetup) {
    var props = {
      fullReportAndDashboardAccess: false,
      systemConfiguration: {
        scheduledInstances: {
          value: false,
          tenantAccess: 0
        },
        tenantAccess: 0
      },
      tenantSetup: {
        actions: {
          create: false,
          edit: false,
          del: false,
          tenantAccess: 0
        },
        permissions: {
          value: false,
          tenantAccess: 0
        },
        tenantAccess: 0
      },
      dataSetup: {
        dataConnectors: {
          value: false,
          tenantAccess: 0
        },
        dataModel: {
          value: false,
          tenantAccess: 0,
          customView: {
            create: false,
            edit: false,
            delete: false,
            tenantAccess: 0
          }
        },
        advancedSettings: {
          category: false,
          others: false,
          tenantAccess: 0
        },
        import: {
          value: false,
          tenantAccess: 0
        },
        tenantAccess: 0
      },
      userSetup: {
        userRoleAssociation: {
          value: false,
          tenantAccess: 0
        },
        actions: {
          create: false,
          edit: false,
          del: false,
          configureSecurityOptions: false,
          tenantAccess: 0
        },
        tenantAccess: 0
      },
      roleSetup: {
        actions: {
          create: false,
          edit: false,
          del: false,
          tenantAccess: 0
        },
        dataModelAccess: {
          value: false,
          tenantAccess: 0
        },
        permissions: {
          value: false,
          tenantAccess: 0
        },
        grantRoleWithFullReportAndDashboardAccess: {
          value: false,
          tenantAccess: 0
        },
        tenantAccess: 0
      },
      reports: {
        canCreateNewReport: {
          value: false,
          tenantAccess: 0
        },
        dataSources: {
          simpleDataSources: false,
          advancedDataSources: false,
          tenantAccess: 0
        },
        reportPartTypes: {
          chart: false,
          form: false,
          gauge: false,
          map: false,
          tenantAccess: 0,
          isEnableGoogleAddress: false
        },
        reportCategoriesSubcategories: {
          canCreateNewCategory: {
            value: false,
            tenantAccess: 0
          },
          categoryAccessibility: {
            categories: m.asFlat([]),
            tenantAccess: 0
          }
        },
        filterProperties: {
          filterLogic: false,
          crossFiltering: false,
          tenantAccess: 0
        },
        fieldProperties: {
          customURL: false,
          embeddedJavaScript: false,
          subreport: false,
          tenantAccess: 0
        },
        actions: {
          schedule: false,
          email: false,
          viewReportHistory: false,
          del: false,
          registerForAlerts: false,
          print: false,
          unarchiveReportVersions: false,
          overwriteExistingReport: false,
          subscribe: false,
          exporting: false,
          configureAccessRights: false,
          tenantAccess: 0
        },
        tenantAccess: 0
      },
      dashboards: {
        canCreateNewDashboard: {
          value: false,
          tenantAccess: 0
        },
        displayDashboardTileHeader: {
          value: false,
          tenantAccess: 0
        },
        dashboardCategoriesSubcategories: {
          canCreateNewCategory: {
            value: false,
            tenantAccess: 0
          },
          categoryAccessibility: {
            categories: m.asFlat([]),
            tenantAccess: 0
          }
        },
        actions: {
          schedule: false,
          email: false,
          del: false,
          subscribe: false,
          print: false,
          overwriteExistingDashboard: false,
          configureAccessRights: false,
          exporting: false,
          tenantAccess: 0
        },
        tenantAccess: 0
      },
      access: {
        accessLimits: {
          value: m.asFlat([]),
          tenantAccess: 0
        },
        accessDefaults: {
          value: m.asFlat([]),
          tenantAccess: 0
        },
        tenantAccess: 0
      },
      scheduling: {
        schedulingLimits: {
          value: m.asFlat([]),
          tenantAccess: 0
        },
        schedulingScope: {
          systemUsers: false,
          externalUsers: false,
          tenantAccess: 0
        },
        tenantAccess: 0
      },
      emailing: {
        deliveryMethod: {
          link: false,
          embeddedHTML: false,
          attachment: false,
          tenantAccess: 0
        },
        attachmentType: {
          word: false,
          excel: false,
          pdf: false,
          csv: false,
          xml: false,
          json: false,
          definition: false,
          tenantAccess: 0
        },
        tenantAccess: 0
      },
      exporting: {
        exportingFormat: {
          word: false,
          excel: false,
          pdf: false,
          csv: false,
          xml: false,
          json: false,
          queryExecution: false,
          definition: false,
          iframe: false,
          tenantAccess: 0
        },
        tenantAccess: 0
      },
      systemwide: {
        canSeeSystemMessages: {
          value: false,
          tenantAccess: 0
        },
        tenantAccess: 0
      },

      section: null,
      isTenantSetup: isTenantSetup
    };

    this.schedulingLimitsTree = [];
    this.accessLimitsTree = [];
    this.isClickedSection = false;
    this.propsCloned = cloneDeep(props);
    m.extendObservable(trackable(this), props);
    data && this.populateData(data, this.propsCloned, this);
  }

  /**
   * populate data
   * @param {object} data
   */
  PermissionDetail.prototype.populateData = m.action(function(data, props, permissionDetail) {
    for (var key in omit(['section', 'isTenantSetup'], props)) {
      var newData = isNil(data) ? this.propsCloned[key] : data[key];

      if (isPlainObject(props[key])) {
        this.populateData(newData, props[key], permissionDetail[key]);
      } else {
        permissionDetail[key] = !isNil(newData) && !isNil(newData.value) ? newData.value : newData;
      }
    }
    this.resetDirty && this.resetDirty();
  });

  /**
   * Update view section
   * @param {String} section
   */
  PermissionDetail.prototype.updateSection = function(section) {
    this.section = section;
  };

  /**
   * Update tenant access parent
   * @param {object} permission section
   * @param {Boolean} isChecked
   */
  PermissionDetail.prototype.updateTenantAccessParent = m.action(function(permissionSection, isChecked) {
    for (var key in permissionSection) {
      var d = permissionSection[key];

      if (!isArrayLike(d)) {
        if (isObject(d)) {
          this.updateTenantAccessParent(d, isChecked);
        } else if (key === 'tenantAccess') {
          permissionSection.tenantAccess = isChecked ? 1 : 0;
        }
      }
    }

    this.markDirty();
  });

  /**
   * Update report actions schedule
   * @param {Object} permission section
   * @param {Boolean} isChecked
   */
  PermissionDetail.prototype.updateReportActionSchedule = m.action(function(permissionSection, isChecked) {
    this.updateData(permissionSection, 'schedule', isChecked);
    !isChecked && this.updateData(permissionSection, 'registerForAlerts', isChecked);
  });

  /**
   * Update user setup edit
   * @param {Object} permission section
   * @param {Boolean} isChecked
   */

  PermissionDetail.prototype.updateUserSetupEdit = m.action(function(permissionSection, isChecked) {
    this.updateData(permissionSection, 'edit', isChecked);
    !isChecked && this.updateData(permissionSection, 'configureSecurityOptions', isChecked);
  });

  // Custom View settings.
  /**
   * Update tenant access parent for data setup (custom view)
   * @param {object} permission section
   * @param {Boolean} input
   */
  PermissionDetail.prototype.updateTenantAccessParentDataSetup = m.action(function(permissionSection, input) {
    const isChecked = input.target ? input.target.checked : input;
    for (var key in permissionSection) {
      var d = permissionSection[key];

      if (!isArrayLike(d)) {
        if (isObject(d)) {
          this.updateTenantAccessParentDataSetup(d, isChecked);
        } else if (key === 'tenantAccess') {
          permissionSection.tenantAccess = isChecked ? 1 : 0;
        }
      }
    }

    this.markDirty();
  });

  /**
   * Update tenant access parent from Child (Custom View)
   * @param {Object} permissionSection section
   */
  PermissionDetail.prototype.updateTenantAccessParentFromChild = m.action(function(permissionSection) {
    let section = permissionSection;

    if (permissionSection.dataModel.value === false) {
      section = omit('dataModel.customView', permissionSection);
    }

    const tenantAccesses = this.getTenantAccesses(section);
    const hasTenantAccesses = filter(tenantAccess => tenantAccess === 1, tenantAccesses);

    if (hasTenantAccesses.length === 0) {
      permissionSection.tenantAccess = 0;
    } else if (hasTenantAccesses.length === tenantAccesses.length) {
      permissionSection.tenantAccess = 1;
    } else {
      permissionSection.tenantAccess = 2;
    }
  });

  /**
   * Update tenant access for Data Setup (Custom View)
   * @param {Object} permission section
   * @param {Object} data tenant access
   * @param {Boolean} isChecked
   */
  PermissionDetail.prototype.updateTenantAccessDataModel = m.action(function(permissionSection, data, input) {
    const isChecked = input.target.checked;
    if (!isChecked && permissionSection.dataModel.customView.tenantAccess) {
      permissionSection.dataModel.customView.tenantAccess = 0;
    }
    data.tenantAccess = isChecked ? 1 : 0;
    this.updateTenantAccessParentFromChild(permissionSection);

    this.markDirty();
  });

  /**
   * Update tenant access for Data Setup (Custom View)
   * @param {Object} permission section
   * @param {Object} data tenant access
   * @param {Boolean} isChecked
   */
  PermissionDetail.prototype.updateTenantAccessDataSetup = m.action(function(permissionSection, data, input) {
    const isChecked = input.target.checked;
    data.tenantAccess = isChecked ? 1 : 0;
    this.updateTenantAccessParentFromChild(permissionSection);

    this.markDirty();
  });

  /**
   * Update tenant access for Data Setup (Custom View)
   * @param {Object} permissionSection section
   * @param {Object} data tenant access
   * @param {Boolean} input
   */
  PermissionDetail.prototype.updateTenantAccessCustomView = m.action(function(permissionSection, data, input) {
    const isChecked = input.target.checked;
    data.tenantAccess = isChecked ? 1 : 0;
    if (isChecked) {
      permissionSection.dataModel.tenantAccess = 1;
    }
    this.updateTenantAccessParentFromChild(permissionSection);

    this.markDirty();
  });

  /**
   * Update data for Data Setup property (Custom View)
   * @param {Object} data section
   * @param {Object} value tenant access
   * @param {Boolean} input
   */
  PermissionDetail.prototype.updateDataSetup = m.action(function(data, value, input) {
    data[value] = input.target.checked;
    this.markDirty();
  });

  /**
   * Update data for Data Model property (Custom View)
   * @param {Object} permission section
   * @param {Object} data tenant access
   * @param {Boolean} input
   */
  PermissionDetail.prototype.updateDataModel = m.action(function(permission, value, input) {
    const data = permission.dataModel;
    const isChecked = input.target.checked;
    data[value] = isChecked;
    if (!isChecked) {
      data.customView.tenantAccess = 0;
      data.customView.create = false;
      data.customView.delete = false;
      data.customView.edit = false;
    }
    this.updateTenantAccessParentFromChild(permission);

    this.markDirty();
  });

  /**
   * Update tenant access
   * @param {Object} permission section
   * @param {Object} permission tenant access
   * @param {Boolean} isChecked
   */
  PermissionDetail.prototype.updateTenantAccess = m.action(function(permissionSection, data, isChecked) {
    data.tenantAccess = isChecked ? 1 : 0;
    var tenantAccesses = this.getTenantAccesses(permissionSection);
    var hasTenantAccesses = filter(function(ta) {
      return ta === 1;
    }, tenantAccesses);

    if (hasTenantAccesses.length === 0) {
      permissionSection.tenantAccess = 0;
    } else if (hasTenantAccesses.length === tenantAccesses.length) {
      permissionSection.tenantAccess = 1;
    } else {
      permissionSection.tenantAccess = 2;
    }

    this.markDirty();
  });

  /**
   * Get list of tenant access
   * @param {Object} permission section
   * @param {Boolean} isChecked
   * @returns {Object} list of tenant access object
   */
  PermissionDetail.prototype.getTenantAccesses = m.action(function(permissionSection) {
    var tenantAccesses = [];
    var data = omit(['tenantAccess'], permissionSection);

    this.getTenantAccess(data, tenantAccesses);
    return tenantAccesses;
  });

  /**
   * Get tenant access
   * @param {Object} permission section
   * @param {Object} list of tenant access
   */
  PermissionDetail.prototype.getTenantAccess = m.action(function(permissionSection, tenantAccesses) {
    for (var key in permissionSection) {
      var d = permissionSection[key];

      if (isObject(d)) {
        this.getTenantAccess(d, tenantAccesses);
      } else {
        if (key === 'tenantAccess') {
          tenantAccesses.push(d);
        }
      }
    }
  });

  /**
   * Update data for property
   * @param {Object} permission tenant access
   * @param {Object} isChekced
   */
  PermissionDetail.prototype.updateData = m.action(function(data, value, isChecked) {
    data[value] = isChecked;
    this.markDirty();
  });

  /**
   * Update list access default
   * @param {array} list access
   */
  PermissionDetail.prototype.updateAccessDefault = m.action(function(data) {
    this.access.accessDefaults.value = data;
  });

  /**
   * Clean model dirty
   */
  PermissionDetail.prototype.cleanDirty = function() {
    this.resetDirty();
  };

  /**
   * Toggle report data sources
   * @param {Boolean} checked
   */
  PermissionDetail.prototype.toggleReportDataSources = m.action(function(checked) {
    this.reports.dataSources.simpleDataSources = checked;
    this.reports.dataSources.advancedDataSources = !checked;
    this.markDirty();
  });

  /**
   * Update access for all data setup section
   * @param: {Object} section
   * @param: {Object} tenantPermission
   * @param: {Boolean} isChecked
   */
  PermissionDetail.prototype.updateAllRoleSection = m.action('Update all role section', function(
    section,
    tenantPermission,
    isChecked,
    parentKey
  ) {
    if (!section)
      return;
    const tenantPermissionOmitList = getOmitList(tenantPermission);
    for (let key in omit(tenantPermissionOmitList, section)) {
      if (
        isBoolean(section[key]) &&
        (key === 'fullReportAndDashboardAccess' || checkTenantPermissionByKey(tenantPermission, key))
      ) {
        section[key] = isChecked;
      } else if (isPlainObject(section[key])) {
        this.updateAllRoleSection(section[key], get(key, tenantPermission), isChecked, key);
      }
    }

    this.markDirty();
  });

  /**
   * check all item in section is check
   * @param  {[type]}  permissionSetion
   * @param  {[type]}  tenantPermisionSection
   * @return {Boolean}
   */
  PermissionDetail.prototype.isAllRoleSectionChecked = function(permissionSetion, tenantPermisionSection) {
    var result = true;

    /**
     * Check all item is checked
     * @param  {Object} section
     * @return {Boolean}
     */
    function checkShow(section, tenantPermision) {
      for (var key in omit(getOmitList(tenantPermision), section)) {
        if (
          section[key] === false &&
          (checkTenantPermissionByKey(tenantPermision, key) || key === 'fullReportAndDashboardAccess')
        ) {
          result = false;
        } else if (isPlainObject(section[key])) {
          checkShow(section[key], get(key, tenantPermision));
        }
      }
    }

    checkShow(permissionSetion, tenantPermisionSection);
    return result;
  };

  /**
   * check all item in section is check
   * @param  {[type]}  permissionSetion
   * @param  {[type]}  tenantPermisionSection
   * @return {Boolean}
   */
  PermissionDetail.prototype.isAllRoleSectionIndeterminate = function(permissionSetion, tenantPermisionSection) {
    var haveAllPermission = true;
    var hasPermission = false;

    /**
     * Check checkShowIndeterminate
     * @param  {Object} section
     * @return {Boolean}
     */
    function checkShowIndeterminate(section, tenantPermision) {
      for (var key in omit(getOmitList(tenantPermision), section)) {
        if (
          section[key] === false &&
          (key === 'fullReportAndDashboardAccess' || checkTenantPermissionByKey(tenantPermision, key))
        ) {
          haveAllPermission = false;
        } else if (section[key] === true) {
          hasPermission = true;
        } else if (isPlainObject(section[key])) {
          checkShowIndeterminate(section[key], get(key, tenantPermision));
        }
      }
    }

    checkShowIndeterminate(permissionSetion, tenantPermisionSection);

    return hasPermission === true && haveAllPermission === false;
  };

  /**
   * Update access for all data setup section
   * @param: {Object} section
   * @param: {Boolean} isChecked
   */
  PermissionDetail.prototype.updateAllTenantSection = m.action('Update all tenant section', function(
    section,
    isChecked
  ) {
    for (var key in omit(TENANT_EXCLUDED, section)) {
      if (isBoolean(section[key])) {
        section[key] = isChecked;
      } else if (isPlainObject(section[key])) {
        this.updateAllTenantSection(section[key], isChecked);
      }
    }
    section.dataModel && this.updateTenantAccessParentFromChild(section);

    this.markDirty();
  });

  /**
   * check all item in section is check
   * @param  {[type]}  permissionSetion
   * @return {Boolean}
   */
  PermissionDetail.prototype.isAllTenantSectionChecked = function(permissionSetion) {
    var result = true;
    /**
     * Check all item is checked
     * @param  {Object} section
     * @return {Boolean}
     */
    function checkAllItemChecked(section) {
      for (var key in omit(TENANT_EXCLUDED, section)) {
        if (section[key] === false) {
          result = false;
        } else if (isPlainObject(section[key])) {
          checkAllItemChecked(section[key]);
        }
      }
    }

    checkAllItemChecked(permissionSetion);
    return result;
  };

  /**
   * check all item in section is check
   * @param  {[type]}  permissionSetion
   * @return {Boolean}
   */
  PermissionDetail.prototype.isAllTenantSectionIndeterminate = function(permissionSetion) {
    var haveAllPermission = true;
    var hasPermission = false;
    /**
     * Check checkShowIndeterminate
     * @param  {Object} section
     * @return {Boolean}
     */
    function checkShowIndeterminate(section) {
      for (var key in omit(TENANT_EXCLUDED, section)) {
        if (section[key] === false) {
          haveAllPermission = false;
        } else if (section[key] === true) {
          hasPermission = true;
        } else if (isPlainObject(section[key])) {
          checkShowIndeterminate(section[key]);
        }
      }
    }

    checkShowIndeterminate(permissionSetion);

    return hasPermission === true && haveAllPermission === false;
  };

  /**
   * Check is all tenant access have check
   * @param:    {Object}  permissionnSection
   * @returns:  {Boolean} result
   */
  PermissionDetail.prototype.isAllTenantAccessChecked = function(permissionSection) {
    var result = true;
    var section = permissionSection;

    // Remove custom view when data model is not checked
    if (permissionSection.dataSetup.dataModel.value === false) {
      section = omit('dataSetup.dataModel.customView', permissionSection);
    }
    /**
     * Check all tenant access have check
     */
    function checkAllTenantAccessChecked(section) {
      for (var key in omit(TENANT_ACCESS_EXCLUDE, section)) {
        if (key === 'tenantAccess' && (section[key] === 0 || section[key] === 2)) {
          result = false;
        } else if (isPlainObject(section[key])) {
          checkAllTenantAccessChecked(section[key]);
        }
      }
    }

    checkAllTenantAccessChecked(section);

    return result;
  };

  /**
   * Check tenant access is indeterminate
   * @param: {Object}   permissionSection
   * @param: {Boolean}  result
   */
  PermissionDetail.prototype.isAllTenantIndeterminate = function(permissionSection) {
    var haveAllPermission = true;
    var hasAtLeastOnePermission = false;
    var result = false;
    var section = permissionSection;

    // Remove custom view when data model is not checked
    if (permissionSection.dataSetup.dataModel.value === false) {
      section = omit('dataSetup.dataModel.customView', permissionSection);
    }

    function checkAllItemIndeterminate(section) {
      for (var key in omit(TENANT_ACCESS_EXCLUDE, section)) {
        if (result) {
          break;
        }
        if (key === 'tenantAccess' && section[key] === 2) {
          result = true;
          break;
        } else if (key === 'tenantAccess' && section[key] === 1) {
          if (!haveAllPermission) {
            result = true;
            break;
          }
          hasAtLeastOnePermission = true;
        } else if (key === 'tenantAccess' && section[key] === 0) {
          if (hasAtLeastOnePermission) {
            result = true;
            break;
          }
          haveAllPermission = false;
        } else if (isPlainObject(section[key])) {
          checkAllItemIndeterminate(section[key]);
        }
      }
    }

    checkAllItemIndeterminate(section);

    return result;
  };

  /**
   * Update all tenant access
   * @param: {Object}   section
   * @param: {Boolean}  isChecked
   */
  PermissionDetail.prototype.updateAllTenantAccess = m.action('updateAllTenantAccess', function(section, isChecked) {
    var value = isChecked ? 1 : 0;

    for (var key in omit(['propsCloned'], section)) {
      if (key === 'tenantAccess') {
        section[key] = value;
      } else if (isPlainObject(section[key])) {
        this.updateAllTenantAccess(section[key], isChecked);
      }
    }

    this.markDirty();
  });

  PermissionDetail.prototype.updateFullReportAndDashBoardAccess = m.action(
    'updateFullReportAndDashBoardAccess',
    function(isChecked) {
      this.updateData(this, 'fullReportAndDashboardAccess', isChecked);
      var updateAllFunction = this.isTenantSetup ? this.updateAllTenantSection : this.updateAllRoleSection;

      // Select all Report selection
      updateAllFunction.call(this, get('reports', this), this, isChecked);
      // Select all Dashboard section
      updateAllFunction.call(this, get('dashboards', this), this, isChecked);
      // Select all Exporting section
      updateAllFunction.call(this, get('exporting', this), this, isChecked);
      // Select all Emailing section
      updateAllFunction.call(this, get('emailing', this), this, isChecked);
    }
  );

  module.exports = PermissionDetail;
})();



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.PermissionDetail.js